import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import * as api from "../api/disciplio-api";
import { useDisciplio } from "./disciplio-context";
import { useI18n } from "./i18n-context";
import { useOnboarding } from "./onboarding-context";
import { publicLegalUrl } from "../legal/public-legal-links";
import { Presence, presenceStateProps, usePresenceState } from "../components/ui/presence";

type AiConsentContextValue = { requestAiAccess: () => Promise<boolean>; open: boolean };
const AiConsentContext = createContext<AiConsentContextValue | null>(null);

export function AiConsentProvider({ children }: { children: ReactNode }) {
  const { state, saveSettings, saveProfile } = useDisciplio();
  const { language, t } = useI18n();
  const { refreshJourney } = useOnboarding();
  const [open, setOpen] = useState(false);
  const resolversRef = useRef(new Set<(value: boolean) => void>());

  const decide = useCallback(async (decision: "ACCEPTED" | "DECLINED") => {
    let allowed = false;
    try {
      const status = await api.updateAiConsent("ai-disclosure-v1.0", decision);
      if (status) {
        saveProfile({ ...state.profile, legal: { ...state.profile.legal!, ...status, termsAcceptedAt: status.termsAcceptedAt ?? undefined, privacyAcceptedAt: status.privacyAcceptedAt ?? undefined, aiDisclosureVersion: status.aiDisclosureVersion ?? undefined, aiDisclosureRespondedAt: status.aiDisclosureRespondedAt ?? undefined } });
        saveSettings({ ...state.settings, aiConsentStatus: decision, aiDisclosureVersion: "ai-disclosure-v1.0", aiDisclosureRespondedAt: new Date().toISOString() });
      }
      allowed = decision === "ACCEPTED";
      void refreshJourney();
    } catch {
      allowed = false;
    } finally {
      const resolvers = [...resolversRef.current];
      resolversRef.current.clear();
      resolvers.forEach((resolve) => resolve(allowed));
      setOpen(false);
    }
  }, [refreshJourney, saveProfile, saveSettings, state.profile, state.settings]);

  const requestAiAccess = useCallback(() => {
    if (state.settings.aiConsentStatus === "ACCEPTED") return Promise.resolve(true);
    if (state.settings.aiConsentStatus === "DECLINED") return Promise.resolve(false);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolversRef.current.add(resolve);
    });
  }, [state.settings.aiConsentStatus]);

  const value = useMemo(() => ({ requestAiAccess, open }), [open, requestAiAccess]);
  return (
    <AiConsentContext.Provider value={value}>
      {children}
      <Presence open={open} exitDurationMs={180}>
        {open ? <AiConsentSurface decide={decide} language={language} t={t} /> : null}
      </Presence>
    </AiConsentContext.Provider>
  );
}

function AiConsentSurface({
  decide,
  language,
  t,
}: {
  decide: (decision: "ACCEPTED" | "DECLINED") => void;
  language: "en" | "pl" | "uk";
  t: ReturnType<typeof useI18n>["t"];
}) {
  const presenceState = usePresenceState();
  return (
    <div className="ai-disclosure-backdrop" role="presentation" {...presenceStateProps(presenceState)}>
      <section
        className="card ai-disclosure-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-disclosure-title"
        data-onboarding-target="AI_CONSENT"
      >
        <p className="label">{t("aiConsent.label")}</p>
        <h2 id="ai-disclosure-title">{t("aiConsent.title")}</h2>
        <p>{t("aiConsent.description")}</p>
        <div className="ai-disclosure-dialog__actions">
          <button type="button" className="btn btn--primary" onClick={() => void decide("ACCEPTED")}>
            {t("aiConsent.enable")}
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => void decide("DECLINED")}>
            {t("aiConsent.decline")}
          </button>
        </div>
        <p className="ai-disclosure-dialog__links">
          <a href={publicLegalUrl("privacy", language)} target="_blank" rel="noopener noreferrer">{t("account.privacy")}</a> · <a href={publicLegalUrl("terms", language)} target="_blank" rel="noopener noreferrer">{t("account.terms")}</a>
        </p>
      </section>
    </div>
  );
}

export function useAiConsent() {
  const context = useContext(AiConsentContext);
  if (!context) throw new Error("useAiConsent must be used within AiConsentProvider");
  return context;
}
